import { useState, useEffect } from "react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { Figure, Dataframe } from "~/components/assistant/types";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { ExportProgress } from "./ExportProgress";
import { WorkbenchSelector } from "./WorkbenchSelector";
import { useExportArtifact, ArtifactType } from "./hooks/useExportArtifact";

type ExportStage = "select" | "exporting" | "success" | "error";

interface WorkbenchOption {
  id: number;
  title: string;
}

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  artifact: Figure | Dataframe;
  artifactType: ArtifactType;
  conversationId: string;
}

export function ExportDialog({
  open,
  onClose,
  artifact,
  artifactType,
  conversationId,
}: ExportDialogProps) {
  const [stage, setStage] = useState<ExportStage>("select");
  const [selectedWorkbench, setSelectedWorkbench] = useState<WorkbenchOption | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [exportedFileName, setExportedFileName] = useState<string | undefined>();
  const [workbenchPath, setWorkbenchPath] = useState<string | undefined>();
  const { contextId } = useAssistantContext();
  const { exportArtifact } = useExportArtifact();

  useEffect(() => {
    if (open) {
      setStage("select");
      setSelectedWorkbench(null);
      setError(undefined);
      setExportedFileName(undefined);
      setWorkbenchPath(undefined);
    }
  }, [open]);

  const handleWorkbenchSelect = async (workbench: WorkbenchOption) => {
    setSelectedWorkbench(workbench);
    setStage("exporting");

    try {
      const result = await exportArtifact({
        artifact,
        artifactType,
        conversationId,
        projectId: workbench.id,
        contextId,
      });

      if (result.success) {
        setExportedFileName(result.fileName);
        setWorkbenchPath(result.workbenchPath);
        setStage("success");
      } else {
        setError(result.error);
        setStage("error");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Something went wrong while exporting your file."
      );
      setStage("error");
    }
  };

  const handleRetry = () => {
    setError(undefined);
    setStage("select");
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        {stage === "select" && (
          <WorkbenchSelector onSelect={handleWorkbenchSelect} onCancel={handleClose} />
        )}

        {(stage === "exporting" || stage === "success" || stage === "error") && (
          <ExportProgress
            status={stage as "exporting" | "success" | "error"}
            artifactType={artifactType}
            fileName={exportedFileName}
            workbenchName={selectedWorkbench?.title}
            workbenchPath={workbenchPath}
            error={error}
            onClose={handleClose}
            onRetry={handleRetry}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
